import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Check, XCircle, Clock, Home, Calendar, MapPin, CreditCard, AlertCircle, MessageCircle, Share2 } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { API } from '../config';
import { trackPixel } from '../lib/pixel';

const trackedBookings = new Set();

const BookingConfirmed = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, pending, failed
  const [booking, setBooking] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);

  const bookingId = searchParams.get('booking_id') || searchParams.get('checkout_reference')?.split('-')[1];

  useEffect(() => {
    if (bookingId) {
      const checkPaymentStatus = async () => {
        try {
          const response = await axios.get(`${API}/payment/status/${bookingId}`);
          setPaymentInfo(response.data);

          if (response.data.payment_status === 'paid') {
            setStatus('success');
            if (!trackedBookings.has(bookingId)) {
              trackedBookings.add(bookingId);
              trackPixel('Purchase', {
                value: response.data.amount_paid,
                currency: 'EUR',
                content_name: 'Massage Booking Payment',
                content_category: 'massage_booking',
              });
            }
          } else if (response.data.payment_status === 'failed') {
            setStatus('failed');
          } else {
            setStatus('pending');
            setTimeout(checkPaymentStatus, 3000);
          }

          const bookingResponse = await axios.get(`${API}/bookings/${bookingId}`);
          setBooking(bookingResponse.data);
        } catch (error) {
          setStatus('unknown');
        }
      };
      checkPaymentStatus();
    } else {
      setStatus('success');
    }
  }, [bookingId]);

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <Clock size={40} className="text-sage-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-stone-800 mb-2">
              Verifying Payment...
            </h1>
            <p className="text-stone-600">Please wait while we confirm your payment.</p>
          </div>
        );

      case 'pending':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock size={40} className="text-amber-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-stone-800 mb-2">
              Payment Processing
            </h1>
            <p className="text-stone-600 mb-4">
              Your payment is being processed. This page will update automatically.
            </p>
            <div className="animate-spin w-6 h-6 border-2 border-sage-600 border-t-transparent rounded-full mx-auto"></div>
          </div>
        );

      case 'unknown':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={40} className="text-amber-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-stone-800 mb-2">
              We Couldn't Verify Your Payment
            </h1>
            <p className="text-stone-600 mb-6">
              Your booking request was received, but we couldn't confirm your payment status right now.
              If you completed the payment, you'll receive a confirmation email shortly. If unsure, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => window.location.reload()}
                className="bg-sage-600 hover:bg-sage-700 text-white rounded-full px-6"
                data-testid="retry-payment-check"
              >
                Check Again
              </Button>
              <Button
                onClick={() => window.open('https://wa.me/353857747474', '_blank')}
                variant="outline"
                className="border-sage-600 text-sage-600 rounded-full px-6"
              >
                Contact Us
              </Button>
            </div>
          </div>
        );

      case 'failed':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle size={40} className="text-red-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-stone-800 mb-2">
              Payment Failed
            </h1>
            <p className="text-stone-600 mb-6">
              Unfortunately, your payment could not be processed. Please try again or contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-sage-600 hover:bg-sage-700 text-white rounded-full px-6"
              >
                Try Again
              </Button>
              <Button
                onClick={() => window.open('https://wa.me/353857747474', '_blank')}
                variant="outline"
                className="border-sage-600 text-sage-600 rounded-full px-6"
              >
                Contact Us
              </Button>
            </div>
          </div>
        );

      case 'success':
      default:
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={40} className="text-sage-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-stone-800 mb-2">
              Booking Confirmed!
            </h1>
            <p className="text-stone-600 mb-4">
              Thank you for booking with Dublin Holistic Massage. 
              A confirmation email has been sent to you.
            </p>

            {/* Payment summary banner - unmissable */}
            {booking && paymentInfo && paymentInfo.amount_paid > 0 && (() => {
              const totalDue = booking.final_price ?? booking.service_price;
              const remaining = Math.max(0, totalDue - paymentInfo.amount_paid);
              return remaining > 0.009 ? (
                <div className="bg-amber-100 border-2 border-amber-400 rounded-2xl p-4 mb-6 max-w-md mx-auto" data-testid="deposit-banner">
                  <p className="text-amber-900 font-bold text-lg">€{paymentInfo.amount_paid.toFixed(2)} deposit paid ✓</p>
                  <p className="text-amber-800 font-semibold text-xl mt-1">€{remaining.toFixed(2)} remaining</p>
                  <p className="text-amber-700 text-sm mt-1">Please pay the remaining balance at the studio on the day of your massage.</p>
                </div>
              ) : (
                <div className="bg-sage-100 border-2 border-sage-400 rounded-2xl p-4 mb-6 max-w-md mx-auto" data-testid="paid-full-banner">
                  <p className="text-sage-800 font-bold text-lg">Paid in full — €{paymentInfo.amount_paid.toFixed(2)} ✓</p>
                  <p className="text-sage-700 text-sm mt-1">Nothing to pay at the studio. See you soon!</p>
                </div>
              );
            })()}

            {booking && (
              <div className="bg-stone-50 rounded-2xl p-4 sm:p-6 text-left mb-6 max-w-md mx-auto">
                <h3 className="font-semibold text-stone-800 mb-4 text-center">Your Booking Details</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Calendar size={16} className="text-sage-600" />
                    </div>
                    <div>
                      <p className="text-sm text-stone-500">Date & Time</p>
                      <p className="font-medium text-stone-800">{booking.booking_date} at {booking.booking_time}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin size={16} className="text-sage-600" />
                    </div>
                    <div>
                      <p className="text-sm text-stone-500">Location</p>
                      <p className="font-medium text-stone-800">
                        {booking.location === 'city_centre' ? 'City Centre (Dublin 1)' : 'Clarehall (Dublin 17)'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CreditCard size={16} className="text-sage-600" />
                    </div>
                    <div>
                      <p className="text-sm text-stone-500">Service</p>
                      <p className="font-medium text-stone-800">{booking.service_name}</p>
                      <p className="text-sm text-sage-600">€{(booking.final_price ?? booking.service_price).toFixed(2)}</p>
                    </div>
                  </div>

                  {paymentInfo && paymentInfo.amount_paid > 0 && (
                    <div className="border-t pt-3 mt-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-stone-500">Amount Paid</span>
                        <span className="font-semibold text-sage-600">€{paymentInfo.amount_paid.toFixed(2)}</span>
                      </div>
                      {booking.payment_type === 'deposit' && ((booking.final_price ?? booking.service_price) - paymentInfo.amount_paid) > 0.009 && (
                        <div className="flex justify-between text-sm mt-1">
                          <span className="text-stone-500">Remaining (pay at studio)</span>
                          <span className="font-medium text-stone-700">€{((booking.final_price ?? booking.service_price) - paymentInfo.amount_paid).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Important Info */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 max-w-md mx-auto text-left">
              <div className="flex items-start gap-2">
                <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800 mb-1">Important Reminders</p>
                  <ul className="text-amber-700 space-y-1">
                    <li>• Please arrive 5-10 minutes early</li>
                    <li>• Cancellations require 24 hours notice</li>
                    <li>• Health insurance receipts available on request</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {booking && (
                <Button
                  onClick={() => {
                    const msg = `Just booked a ${booking.service_name} at Dublin Holistic Massage! ${booking.booking_date} at ${booking.booking_time}.\n\nBook here: https://www.dublinholisticmassages.com`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                  }}
                  className="bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full px-6"
                  data-testid="confirmed-share-whatsapp"
                >
                  <MessageCircle size={18} className="mr-2" />
                  Share on WhatsApp
                </Button>
              )}
              <Button
                onClick={() => navigate('/')}
                className="bg-sage-600 hover:bg-sage-700 text-white rounded-full px-6"
              >
                <Home size={18} className="mr-2" />
                Back to Home
              </Button>
              <Button
                onClick={() => window.open('https://wa.me/353857747474', '_blank')}
                variant="outline"
                className="border-sage-600 text-sage-600 rounded-full px-6"
              >
                Contact Us
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-white to-warm-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-sage-700">Dublin Holistic Massage</h2>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8">
          {renderContent()}
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-stone-500">
          <p>📞 +353 85 774 7474</p>
          <p>✉️ dublinholisticmassage@gmail.com</p>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmed;
